import { gql } from "@apollo/client";
import {
  Button,
  Dialog,
  TextField,
  useDialogState,
  useMenuItems,
} from "@resource/atlas";
import {
  atlasCheck,
  serviceAshby,
  serviceGreenhouse,
  serviceLever,
} from "@resource/atlas/icons";
import {
  AtssyncAccountType,
  ConnectAtsAccount,
  ConnectAtsAccountVariables,
  CustomerForIntegrationsPage,
  DisconnectAtsAccount,
  DisconnectAtsAccountVariables,
  V2IntegrationsPage_currentOrganization_customer_atssyncAccounts as AtssyncAccount,
} from "generated/schemaTypes";
import { useState } from "react";
import useMutation from "utils/useMutation";

import { CUSTOMER_FOR_INTEGRATIONS_PAGE_FRAGMENT } from "../__fragments/customer";
import { IntegrationCard } from "./IntegrationCard";

const CONNECT_ATS_ACCOUNT = gql`
  mutation ConnectAtsAccount($input: UpsertAtssyncAccountInput!) {
    upsertAtssyncAccount(input: $input) {
      success
      code
      message
      atssyncAccount {
        id
        type
        apiKeySuffix
      }
    }
  }
`;

const DISCONNECT_ATS_ACCOUNT = gql`
  mutation DisconnectAtsAccount($input: DisconnectAtssyncAccountInput!) {
    disconnectAtssyncAccount(input: $input) {
      success
      code
      message
      atssyncAccount {
        id
      }
    }
  }
`;

const mapAtsToIcon = {
  [AtssyncAccountType.GREENHOUSE]: serviceGreenhouse,
  [AtssyncAccountType.LEVER]: serviceLever,
  [AtssyncAccountType.ASHBY]: serviceAshby,
};

type Props = {
  atsType: AtssyncAccountType;
  customer: CustomerForIntegrationsPage;
};

export function AtsIntegrationCard(props: Props) {
  const { atsType, customer } = props;
  const { atssyncAccounts } = customer;
  const account = atssyncAccounts.find((a) => a.type === atsType);
  const readableAtsType = atsType.charAt(0) + atsType.slice(1).toLowerCase();
  const [apiKey, setApiKey] = useState("");
  const [connectAccount, { loading: connectAccountLoading }] = useMutation<
    ConnectAtsAccount,
    ConnectAtsAccountVariables
  >(CONNECT_ATS_ACCOUNT, {
    variables: {
      input: {
        type: atsType,
        apiKey,
      },
    },
    update: (cache, { data }) => {
      cache.updateFragment(
        {
          id: `Customer:${customer.id}`,
          fragment: CUSTOMER_FOR_INTEGRATIONS_PAGE_FRAGMENT,
        },
        (existingCustomer) => {
          return {
            ...existingCustomer,
            atssyncAccounts: [
              ...(existingCustomer.atssyncAccounts || []),
              data?.upsertAtssyncAccount?.atssyncAccount,
            ],
          };
        }
      );
    },
  });
  const [disconnectAccount, { loading: disconnectAccountLoading }] =
    useMutation<DisconnectAtsAccount, DisconnectAtsAccountVariables>(
      DISCONNECT_ATS_ACCOUNT,
      {
        update: (cache, { data }) => {
          cache.updateFragment(
            {
              id: `Customer:${customer.id}`,
              fragment: CUSTOMER_FOR_INTEGRATIONS_PAGE_FRAGMENT,
            },
            (existingCustomer) => {
              return {
                ...existingCustomer,
                atssyncAccounts: existingCustomer.atssyncAccounts?.filter(
                  (a: AtssyncAccount) =>
                    a.id !== data?.disconnectAtssyncAccount?.atssyncAccount?.id
                ),
              };
            }
          );
        },
      }
    );
  const dialogState = useDialogState();
  const disconnectConfirmationDialogState = useDialogState();
  const menuItems = useMenuItems(
    (i) => [
      i.item({
        children: "Settings",
        key: "settings",
        onSelect: () => {
          dialogState.setOpen(true);
        },
      }),
      i.separator({
        key: "sep",
      }),
      i.item({
        children: "Disconnect",
        key: "disconnect",
        onSelect: () => {
          disconnectConfirmationDialogState.setOpen(true);
        },
        isDestructive: true,
      }),
    ],
    [dialogState, disconnectConfirmationDialogState]
  );

  return (
    <div>
      <IntegrationCard
        name={readableAtsType}
        icon={mapAtsToIcon[atsType]}
        subText={`Connect Guide with ${readableAtsType} to manage candidates.`}
        buttonProps={{
          onClick: () => dialogState.setOpen(true),
          children: account ? "Connected" : "Connect",
          variant: account ? "success" : "default",
          icon: account ? atlasCheck : undefined,
        }}
        menuItems={account ? menuItems : null}
      />
      <Dialog.Root state={dialogState}>
        <Dialog.Content
          header={{
            title: account ? "Connected" : `Connect ${readableAtsType}`,
          }}
          footer={{
            leftActions: <Button onClick={dialogState.hide}>Cancel</Button>,
            rightActions: (
              <Button
                onClick={() =>
                  connectAccount({
                    onCompleted: () => {
                      dialogState.hide();
                    },
                  })
                }
                isLoading={connectAccountLoading}
              >
                Connect
              </Button>
            ),
          }}
        >
          <div className="h-full py-4">
            <TextField
              onChange={setApiKey}
              label={`${readableAtsType} API Key`}
              placeholder={
                account
                  ? `XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXX${account.apiKeySuffix}`
                  : `Enter your ${readableAtsType} API Key`
              }
            />
          </div>
        </Dialog.Content>
      </Dialog.Root>
      {account && (
        <Dialog.Root state={disconnectConfirmationDialogState}>
          <Dialog.Content
            header={{
              title: `Disconnect ${readableAtsType}`,
            }}
            footer={{
              leftActions: (
                <Button onClick={disconnectConfirmationDialogState.hide}>
                  Cancel
                </Button>
              ),
              rightActions: (
                <Button
                  onClick={() =>
                    disconnectAccount({
                      variables: {
                        input: {
                          id: account?.id,
                        },
                      },
                      onCompleted: () => {
                        disconnectConfirmationDialogState.hide();
                      },
                    })
                  }
                  isLoading={disconnectAccountLoading}
                  variant="danger"
                >
                  Disconnect
                </Button>
              ),
            }}
          >
            <p className="text-body-md">
              Disconnecting from {readableAtsType} will stop syncing updates
              from your ATS. Are you sure you want to disconnect?
            </p>
          </Dialog.Content>
        </Dialog.Root>
      )}
    </div>
  );
}
